import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Collapse from '@mui/material/Collapse'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import StopIcon from '@mui/icons-material/Stop'
import ReplayIcon from '@mui/icons-material/Replay'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import VideoFileIcon from '@mui/icons-material/VideoFile';

import StreamingManagementService from '../../services/StreamingManagementService'
import { StreamingItem, StreamingListFilters } from '../types'
import { StreamingStatusBadge } from './StatusBadge'
import { ForceStopModal, RetryModal, DismissModal } from './ActionModals'
import StreamingDetailModal from './StreamingDetailModal'

const STATUS_GROUPS = [
  { label: 'Todos', value: '' },
  { label: 'Em andamento', value: 'running,initializing,created_hiplade' },
  { label: 'Com erro', value: 'error_rca,error_gcp_create_streaming,error_gcp_rca,error_gcp_no_main_camera,error_merdiaservice_create_streaming' },
  { label: 'Parado manualmente', value: 'force_stopped' },
  { label: 'Concluídos', value: 'processing_recording,not_corrected' },
  { label: 'Descartados', value: 'dismissed' },
]

const DEFAULT_FILTERS: StreamingListFilters = {
  status: '',
  from: '',
  to: '',
  per_page: 20,
  page: 1,
}

export default function StreamingList() {
  const [items, setItems] = useState<StreamingItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<StreamingListFilters>(DEFAULT_FILTERS)
  const [showFilters, setShowFilters] = useState(false)

  const [forceStopTarget, setForceStopTarget] = useState<string | null>(null)
  const [retryTarget, setRetryTarget] = useState<string | null>(null)
  const [dismissTarget, setDismissTarget] = useState<string | null>(null)
  const [detailTarget, setDetailTarget] = useState<StreamingItem | null>(null)

  const fetchData = useCallback(async (f: StreamingListFilters) => {
    setLoading(true)
    setError(null)
    try {
      const res = await StreamingManagementService.getStreamings(f)
      if (res?.data) {
        setItems(res.data.data)
        setTotal(res.data.total)
      }
    } catch {
      setError('Não foi possível carregar os streamings.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData(filters)
  }, [])

  const applyFilters = () => {
    const updated = { ...filters, page: 1 }
    setFilters(updated)
    fetchData(updated)
  }

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS)
    fetchData(DEFAULT_FILTERS)
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    const updated = { ...filters, page: newPage + 1 }
    setFilters(updated)
    fetchData(updated)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = { ...filters, per_page: parseInt(e.target.value, 10), page: 1 }
    setFilters(updated)
    fetchData(updated)
  }

  const handleTranscode = async (streamingId: string) => {
    try {
      const res = await StreamingManagementService.transcode(streamingId)
      if (res) {
        console.log(res)
      }
    } catch {
      setError('Não foi possível realizar a transcodificação.')
    }
  }

  const handleActionSuccess = () => fetchData(filters)

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6">Histórico de Streamings</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters((v) => !v)}
          >
            Filtros
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => fetchData(filters)}
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      {/* Filter panel */}
      <Collapse in={showFilters}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status ?? ''}
                label="Status"
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as string }))}
              >
                {STATUS_GROUPS.map((g) => (
                  <MenuItem key={g.value} value={g.value}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Data inicial"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.from ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            />
            <TextField
              size="small"
              label="Data final"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.to ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            />

            <Box display="flex" gap={1}>
              <Button variant="contained" size="small" onClick={applyFilters}>
                Filtrar
              </Button>
              <Button variant="outlined" size="small" onClick={resetFilters}>
                Limpar
              </Button>
            </Box>
          </Box>
        </Paper>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Evento</TableCell>
                  <TableCell>Sala / Câmera</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Atualizado em</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>
                        Nenhum registro encontrado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {items.map((item) => (
                  <TableRow key={item.streaming_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.event_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.room_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.camera_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StreamingStatusBadge status={item.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(item.updated_at).toLocaleString('pt-BR')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        {item.available_actions.includes('view') && (
                          <Tooltip title="Detalhes">
                            <IconButton size="small" onClick={() => setDetailTarget(item)}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.available_actions.includes('force_stop') && (
                          <Tooltip title="Forçar Parada">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setForceStopTarget(item.streaming_id)}
                            >
                              <StopIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.available_actions.includes('retry') && (
                          <Tooltip title="Tentar Novamente">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => setRetryTarget(item.streaming_id)}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.available_actions.includes('dismiss') && (
                          <Tooltip title="Descartar">
                            <IconButton
                              size="small"
                              onClick={() => setDismissTarget(item.streaming_id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.available_actions.includes('transcode') &&
                          item.status === 'processing_recording' &&
                          item.video_identifier == null &&
                          item.transcode_job_id == null && (
                          <Tooltip title="Transcodificar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleTranscode(item.streaming_id)}
                            >
                              <VideoFileIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {item.available_actions.includes('view_recording') && item.recording_url && (
                          <Tooltip title="Ver Gravação">
                            <IconButton
                              size="small"
                              color="primary"
                              component="a"
                              href={item.recording_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <VideoLibraryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={(filters.page ?? 1) - 1}
            rowsPerPage={filters.per_page ?? 20}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Paper>
      )}

      <ForceStopModal
        open={!!forceStopTarget}
        streamingId={forceStopTarget}
        onClose={() => setForceStopTarget(null)}
        onSuccess={handleActionSuccess}
      />
      <RetryModal
        open={!!retryTarget}
        streamingId={retryTarget}
        onClose={() => setRetryTarget(null)}
        onSuccess={handleActionSuccess}
      />
      <DismissModal
        open={!!dismissTarget}
        streamingId={dismissTarget}
        onClose={() => setDismissTarget(null)}
        onSuccess={handleActionSuccess}
      />
      <StreamingDetailModal
        open={!!detailTarget}
        item={detailTarget}
        onClose={() => setDetailTarget(null)}
      />
    </Box>
  )
}
